/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react'

// react-router components
import { useLocation, Link } from 'react-router-dom'

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types'

// @mui core components
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import Icon from '@mui/material/Icon'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Fade from '@mui/material/Fade'

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox'
import SoftTypography from 'components/SoftTypography'
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import styled from 'styled-components'
import Breadcrumbs from 'components/Breadcrumbs'

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarRowRight,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from 'components/DashboardNavbar/styles'

// Soft UI Dashboard PRO React context
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from 'context'

//Icons
import ListItemIcon from '@mui/material/ListItemIcon'
import { IntakeContext } from 'context/IntakeContext'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import LockIcon from '@mui/icons-material/Lock'
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CloseIcon from '@mui/icons-material/Close'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'

// Popup
import { selectData } from 'layouts/onboarding'
import { callZapier } from 'utils/zapier/callZapier'
import { NavMenu } from 'components/NavMenu/NavMenu'
import NotificationItem from 'components/NotificationItem'
import { ModalOnboarding } from 'layouts/onboarding/components/ModalOnboarding'
import { steps } from 'layouts/onboarding'
import GaugeComponent from 'react-gauge-component'
import { apiClient } from 'utils/api/apiClient'
import { useRetirementGapCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useRetirementGapCalculations'
import { usePARCOScoreCalculations } from 'layouts/authenticated/pages/dashboard/hooks/usePARCOScoreCalculations'
import Slide from '@mui/material/Slide'
import TextBanner from './textBanner'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

function DashboardNavbar({ absolute, light, isMini }) {
  const { signOut, intakeState, unassume, assumedUser } =
    useContext(IntakeContext)
  const [navbarType, setNavbarType] = useState()
  const [accountMenu, setAccountMenu] = useState(false)
  const [showModalOnboarding, setShowModalOnboarding] = useState(false)
  const [showSectionSteps, setShowSectionSteps] = useState(false)
  const [sectionStepsHeader, setSectionStepsHeader] = useState('Update Section')
  const [sectionTag, setSectionTag] = useState('')
  const [controller, dispatch] = useSoftUIController()
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller
  const remainingOnboardingSteps = useRef(
    steps.filter(
      (step) =>
        step.type === 'Question' &&
        !intakeState[step.props?.id] &&
        (step?.dependencies || []).every(
          ({ attribute, value }) => intakeState[attribute] === value
        )
    )
  )
  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [deductions, setDeductions] = useState([])

  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    const foundAssets = items?.filter(({ type }) => type === 'asset') || []
    setAssets(foundAssets)
    const foundLiabilities =
      items?.filter(({ type }) => type === 'liability') || []
    setLiabilities(foundLiabilities)
    const foundDeductions =
      items?.filter(({ type }) => type === 'deduction') || []
    setDeductions(foundDeductions)
  }, [])

  const sectionSteps = steps.filter((step) => step.tags?.includes(sectionTag))

  const closeSectionSteps = () => {
    setShowSectionSteps(false)
  }

  const openEmploymentSectionSteps = () => {
    setSectionStepsHeader('Update Employment Details')
    setSectionTag('employmentDetails')
    setShowSectionSteps(true)
  }
  const openPayStubSectionSteps = () => {
    setSectionStepsHeader('Update Pay Stub Details')
    setSectionTag('payStubDetails')
    setShowSectionSteps(true)
  }
  const openDeductionSectionSteps = () => {
    setSectionStepsHeader('Update Deduction Details')
    setSectionTag('deductionDetails')
    setShowSectionSteps(true)
  }

  function incompleteSection(obj, attributes) {
    return attributes.reduce((count, attr) => {
      return count + (obj[attr] === null || obj[attr] === '' ? 1 : 0)
    }, 0)
  }

  const employmentDetailsSection = incompleteSection(intakeState, [
    'employmentStatus',
    'agency',
    'positionTitle',
    'dateOfHire',
    'retirementSystem',
    'dateOfPlannedRetirement',
  ])
  const payStubDetailsSection = incompleteSection(intakeState, [
    'baseSalary',
    'highThree',
    'currentLeaveBalance',
    'currentSickLeaveBalance',
    'netTakeHome',
  ])
  const deductionsSection = incompleteSection(intakeState, [
    'tsp',
    'tspBalance',
    'tspLoans',
    'healthInsurance',
    'healthInsurancePremium',
  ])

  const unfinishedSectionTotal = [
    employmentDetailsSection,
    payStubDetailsSection,
    deductionsSection,
  ].filter((value) => value > 0).length

  useEffect(() => {
    let fetched = false
    if (fetched) return

    getAccounts()
    fetched = true
  }, [])
  const {
    adjustedNetTakeHomeInRetirement,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeDifferencePARCOGap,
  } = useRetirementGapCalculations(deductions, liabilities)
  const { parcoReadinessScore4Percent } = usePARCOScoreCalculations(
    assets,
    liabilities,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeInRetirement,
    adjustedNetTakeHomeDifferencePARCOGap
  )
  // console.log(remainingOnboardingSteps)
  // const [openMenu, setOpenMenu] = useState(false)
  const route = useLocation().pathname.split('/').slice(1)

  // const { signOut } = useAuthenticator()

  //Locusive
  // useEffect(() => {
  //   const script = document.createElement('script')
  //   script.src =
  //     'https://embedded-assistant.locusive.com/locusive-assistant.bundle.js'
  //   script.async = true
  //   document.body.appendChild(script)
  //   window.locusiveAssistantConfig = {
  //     channelInstanceId: '57e73eb4-2a27-4214-baaf-42c65c0cd184',
  //   }
  //   return () => {
  //     document.body.removeChild(script)
  //   }
  // }, [])

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType('sticky')
    } else {
      setNavbarType('static')
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      )
    }

    /**
     The event listener that's calling the handleTransparentNavbar function when
     scrolling the window.
    */
    window.addEventListener('scroll', handleTransparentNavbar)

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar()

    // Remove event listener on cleanup
    return () => window.removeEventListener('scroll', handleTransparentNavbar)
  }, [dispatch, fixedNavbar])

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav)
  // const handleOpenMenu = (event) => setOpenMenu(event.currentTarget)
  // const handleCloseMenu = () => setOpenMenu(false)
  const handleHelpMenu = (type) => {
    const url =
      type === 'call'
        ? 'https://hooks.zapier.com/hooks/catch/15607509/38ulk8i/'
        : type === 'text'
        ? 'https://hooks.zapier.com/hooks/catch/15607509/38uqhx3/'
        : 'https://hooks.zapier.com/hooks/catch/15607509/38uqcjf/'

    callZapier({
      hookUrl: url,
      user: intakeState,
      payload: {
        user: {
          firstName: intakeState.firstName,
          lastName: intakeState.lastName,
          phoneNumber: intakeState.phone,
          email: intakeState.email,
        },
      },
      sendNotification: true,
      notificationMessage: `A request has been sent to PARCO! Expect a ${type} shortly.`,
    })
  }

  const [anchorEl, setAnchorEl] = useState(null)

  const handleToggleProfile = (e) => {
    setAccountMenu(e.currentTarget)
  }
  const accountMenuIsOpen = Boolean(accountMenu)
  const handleCloseAccountMenu = () => {
    setAccountMenu(null)
  }

  const open = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleCallClose = () => {
    setAnchorEl(null)
    handleHelpMenu('call')
  }
  const handleTextClose = () => {
    setAnchorEl(null)
    handleHelpMenu('text')
  }
  const handleEmailClose = () => {
    setAnchorEl(null)
    handleHelpMenu('email')
  }

  const closeModalOnboarding = useCallback(() => {
    setShowModalOnboarding(false)
    // updating this ref ONLY works because the setShowModalOnboarding
    // updates state and triggers a rerender, which passes this into the
    // modal onboarding component
    remainingOnboardingSteps.current = steps.filter(
      (step) =>
        step.type === 'Question' &&
        step.tags?.includes('onboarding') &&
        !intakeState[step.props?.id] &&
        (step?.dependencies || []).every(
          ({ attribute, value }) => intakeState[attribute] === value
        )
    )
  }, [intakeState])

  const openModalOnboarding = useCallback(() => {
    setShowModalOnboarding(true)
  }, [])
  const improveScoreHelp = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/3zmzqpy/',
      payload: {
        user: {
          firstName: intakeState.firstName,
          lastName: intakeState.lastName,
          phoneNumber: intakeState.phone,
          email: intakeState.email,
        },
      },
      sendNotification: true,
      notificationMessage:
        'A member of the PARCO team will reach out to you shortly! Expect a call to help improve your retirement readiness.',
    })
  }

  const [openDialogModal, setOpenDialogModal] = useState(false)
  const handleDialogModalOpen = () => {
    setOpenDialogModal(true)
  }
  const handleDialogModalClose = () => {
    setOpenDialogModal(false)
  }
  const handleCallMeClose = () => {
    improveScoreHelp()
    setOpenDialogModal(false)
  }
  const handleScheduleCallClose = () => {
    setOpenDialogModal(false)
  }

  const StyledProfileDropdownContainer = styled('div')(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    '@media only screen and (max-width: 768px)': {
      padding: '5px',
    },
  }))
  const StyledScoreContainer = styled('div')(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    paddingRight: '74px',
    height: '40px',
    '@media only screen and (max-width: 768px)': {
      height: '50px',
      paddingRight: '62px',
    },
    '&>.semicircle-gauge': {
      maxWidth: '60px',
      position: 'absolute',
      right: '0',
    },
    '&>.semicircle-gauge>svg>g>g>text': {
      transform: 'translate(0px, -4px)',
    },
    '&>.semicircle-gauge>svg>g>g>circle': {
      transform: `translate(${
        (isNaN(parcoReadinessScore4Percent) ? 0 : parcoReadinessScore4Percent) *
          9 -
        3
      }px, -6px)`,
      filter: 'drop-shadow(0px 2px 1.5px rgba(0,0,0,0.3))',
    },
  }))

  const StyledAgencyLogo = styled.img`
    max-width: 40px;
    max-height: 40px;
    min-height: 20px;
    width: auto;
  `
  return (
    <>
      {/* <TextBanner /> */}
      <span>
        <Dialog
          open={openDialogModal}
          TransitionComponent={Transition}
          onClose={handleDialogModalClose}
          aria-labelledby="improve-score-dialog-title"
          aria-describedby="improve-score-dialog-description"
        >
          <DialogTitle
            id="improve-score-dialog-title"
            sx={{ paddingRight: '40px' }}
          >
            {'A PARCO Team Member is ready to help!'}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleDialogModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#344767',
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent sx={{ paddingTop: '0px' }}>
            <DialogContentText id="improve-score-dialog-description">
              Let us know when we can contact you so we can work on getting you
              retirement ready by your planned retirement date.
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              rel="noreferrer"
              size="large"
              variant="contained"
              sx={[
                {
                  padding: '10px 24px 9px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                  marginBottom: { xs: '8px', sm: '0px' },
                },
                {
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                },
              ]}
              onClick={handleCallMeClose}
            >
              CALL ME NOW
            </Button>
            <Button
              rel="noreferrer"
              size="large"
              variant="contained"
              sx={[
                {
                  padding: '10px 24px 9px',
                  backgroundColor: '#344767',
                  color: '#fff',
                  fontWeight: 'normal',
                  boxShadow: 'none',
                },
                {
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#293852',
                    boxShadow: 'none',
                  },
                },
              ]}
              href="https://team_parco.apptoto.com/"
              target="_blank"
              onClick={handleScheduleCallClose}
            >
              SCHEDULE A CALL
            </Button>
          </DialogActions>
        </Dialog>
        {assumedUser ? (
          <div
            style={{
              backgroundColor: 'red',
              color: '#fff',
              fontSize: '13px',
              zIndex: 1101,
              borderRadius: '4px',
              position: 'sticky',
              top: '0',
            }}
          >
            You have Assumed the Role of{' '}
            {intakeState.firstName && intakeState.lastName
              ? `${intakeState.firstName} ${intakeState.lastName}`
              : '(user)'}
          </div>
        ) : null}
        <AppBar
          position="sticky"
          sx={{
            height: 75,
            backgroundColor: '#fff',
            // boxShadow: '0px -8px 10px #141727',
            zIndex: 15,
            borderBottom: '1px solid #ddd',
            padding: '5px 12px',
          }}
        >
          <Toolbar sx={(theme) => navbarContainer(theme)}>
            <SoftBox
              color="#262627"
              sx={(theme) => navbarRow(theme, { isMini })}
            >
              {/* <Icon
              fontSize="medium"
              sx={navbarDesktopMenu}
              onClick={handleMiniSidenav}
              data-testid="toggleMenuDesktop"
            >
              {miniSidenav ? 'menu_open' : 'menu'}
            </Icon> */}
              <IconButton
                size="large"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
                data-testid="toggleMenuButton"
              >
                <Icon className={light ? 'text-white' : 'text-dark'}>
                  {miniSidenav ? 'menu' : 'menu_open'}
                </Icon>
              </IconButton>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  '& h4': { color: '#262627' },
                }}
              >
                <Breadcrumbs
                  icon="home"
                  title={route[route.length - 1]}
                  route={route}
                  light={light}
                />
              </Box>
            </SoftBox>
            {isMini ? null : (
              <SoftBox sx={(theme) => navbarRowRight(theme, { isMini })}>
                {/* <SoftBox pr={1} width="400px">
              <SoftInput
                placeholder="Type here..."
                icon={{ component: 'search', direction: 'left' }}
              />
            </SoftBox> */}
                <SoftBox>
                  <Button
                    id="fade-button"
                    aria-controls={open ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    endIcon={<KeyboardArrowDownIcon />}
                    disableFocusRipple
                    disableRipple
                    size="large"
                    sx={{
                      color: '#262627',
                      textTransform: 'capitalize',
                      fontSize: '15px',
                      '&:hover': {
                        color: '#262627',
                        transform: 'none',
                      },
                      '&:focus': {
                        color: '#262627 !important',
                      },
                      '@media only screen and (max-width: 575px)': {
                        flexDirection: 'column',
                        '&>span': {
                          margin: 'auto',
                        },
                      },
                    }}
                  >
                    Help
                  </Button>
                  <Menu
                    id="fade-menu"
                    MenuListProps={{
                      'aria-labelledby': 'fade-button',
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    TransitionComponent={Fade}
                  >
                    <MenuItem onClick={handleCallClose}>
                      <ListItemIcon>
                        <PhoneCallbackIcon fontSize="small" />
                      </ListItemIcon>
                      Call Me
                    </MenuItem>
                    <MenuItem onClick={handleTextClose}>
                      <ListItemIcon>
                        <PhoneAndroidIcon fontSize="small" />
                      </ListItemIcon>
                      Text Me
                    </MenuItem>
                    <MenuItem onClick={handleEmailClose}>
                      <ListItemIcon>
                        <EmailOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      Email Me
                    </MenuItem>
                  </Menu>
                </SoftBox>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <NavMenu
                    menuTextOrComponent={
                      <IconButton
                        size="large"
                        aria-controls="notification-menu"
                        aria-haspopup="true"
                        onClick={() => null}
                        sx={{ color: '#262627' }}
                      >
                        <Icon>notifications</Icon>
                      </IconButton>
                    }
                    showCaret={false}
                    menuItems={
                      unfinishedSectionTotal > 2
                        ? [
                            {
                              item: (
                                <NotificationItem
                                  title={['Onboarding Incomplete', '']}
                                  showIcon={false}
                                  image={
                                    <PriorityHighIcon sx={{ color: '#fff' }} />
                                  }
                                  onClick={openModalOnboarding}
                                  date="Click here to complete your onboarding"
                                />
                              ),
                            },
                          ]
                        : [
                            payStubDetailsSection > 0 && {
                              item: (
                                <NotificationItem
                                  title={['Pay Stub Details Incomplete', '']}
                                  showIcon={false}
                                  image={
                                    <AttachMoneyIcon sx={{ color: '#fff' }} />
                                  }
                                  onClick={openPayStubSectionSteps}
                                  date="Click here to update your pay stub information"
                                />
                              ),
                            },
                            employmentDetailsSection > 0 && {
                              item: (
                                <NotificationItem
                                  title={['Employment Details Incomplete', '']}
                                  showIcon={false}
                                  image={
                                    <WorkOutlineIcon sx={{ color: '#fff' }} />
                                  }
                                  onClick={openEmploymentSectionSteps}
                                  date="Click here to update your employment information"
                                />
                              ),
                            },
                            deductionsSection > 0 && {
                              item: (
                                <NotificationItem
                                  title={['Deduction Details Incomplete', '']}
                                  showIcon={false}
                                  image={
                                    <FormatListBulletedIcon
                                      sx={{ color: '#fff' }}
                                    />
                                  }
                                  onClick={openDeductionSectionSteps}
                                  date="Click here to update your deduction information"
                                />
                              ),
                            },
                          ]
                    }
                  ></NavMenu>
                </Box>
                <ModalOnboarding
                  open={showModalOnboarding}
                  handleClose={closeModalOnboarding}
                  modalHeader={'Complete Onboarding'}
                  steps={remainingOnboardingSteps.current}
                />
                <ModalOnboarding
                  open={showSectionSteps}
                  handleClose={closeSectionSteps}
                  modalHeader={sectionStepsHeader}
                  steps={sectionSteps}
                  key={sectionTag}
                />
                <SoftBox
                  color={light ? 'white' : 'inherit'}
                  sx={{
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    padding: '2px 4px',
                    marginRight: '10px',
                  }}
                >
                  <Button
                    disableFocusRipple
                    disableRipple
                    onClick={handleDialogModalOpen}
                    size="large"
                    sx={{
                      color: '#262627',
                      textTransform: 'capitalize',
                      '&:hover': {
                        color: '#262627',
                        transform: 'none',
                      },
                      '&:focus': {
                        color: '#262627 !important',
                      },
                      '@media screen and (max-width: 768px)': {
                        justifyContent: 'flex-start',
                        padding: 0,
                      },
                    }}
                  >
                    <StyledScoreContainer>
                      <SoftTypography
                        variant="body2"
                        sx={{
                          fontSize: '1em',
                          fontWeight: 600,
                          color: '#262627',
                          lineHeight: 1.2,
                          textAlign: 'left',
                          display: { xs: 'none', md: 'block' },
                        }}
                      >
                        Retirement Readiness
                      </SoftTypography>
                      <GaugeComponent
                        type="semicircle"
                        marginInPercent={{
                          top: 0.058,
                          bottom: 0.0,
                          left: 0.0,
                          right: 0.0,
                        }}
                        arc={{
                          colorArray: ['#FD5C70', '#FFCA59', '#4CAF50'],
                          padding: 0.04,
                          width: 0.15,
                          subArcs: [
                            { limit: 55 },
                            { limit: 75 },
                            { limit: 100 },
                          ],
                        }}
                        labels={{
                          valueLabel: {
                            style: {
                              fill: '#262627',
                              textShadow: 'none',
                              fontSize: '120px',
                              fontWeight: 700,
                            },
                            formatTextValue: (value) => {
                              return value
                            },
                          },
                          tickLabels: {
                            hideMinMax: true,
                          },
                        }}
                        pointer={{
                          type: 'blob',
                          animate: false,
                          animationDelay: 0,
                          width: 30,
                        }}
                        value={
                          isNaN(parcoReadinessScore4Percent)
                            ? 0
                            : Math.round(parcoReadinessScore4Percent * 100)
                        }
                      ></GaugeComponent>
                    </StyledScoreContainer>
                  </Button>
                </SoftBox>
                <SoftBox
                  color={light ? 'white' : 'inherit'}
                  sx={{
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    padding: '2px 4px',
                  }}
                >
                  {/* <IconButton size="medium" onClick={handleToggleProfile}>
                  <Avatar sx={{ bgcolor: '#344767' }}>
                    {`${
                      selectData.agency.find(
                        ({ value }) => value === intakeState.agency
                      )?.logo || ''
                    }` === '' ? (
                      intakeState.firstName && intakeState.lastName ? (
                        `${intakeState?.firstName?.[0]}${intakeState?.lastName?.[0]}`
                      ) : (
                        <AccountCircleIcon fontSize="small" />
                      )
                    ) : (
                      <StyledAgencyLogo
                        src={`${
                          selectData.agency.find(
                            ({ value }) => value === intakeState.agency
                          )?.logo || ''
                        }`}
                      />
                    )}
                  </Avatar>
                </IconButton> */}
                  <Button
                    data-testid="account-profile-container"
                    aria-controls={accountMenuIsOpen ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={accountMenuIsOpen ? 'true' : undefined}
                    onClick={handleToggleProfile}
                    // endIcon={<KeyboardArrowDownIcon />}
                    disableFocusRipple
                    disableRipple
                    size="large"
                    sx={{
                      color: '#344767',
                      textTransform: 'capitalize',
                      '&:hover': {
                        color: '#344767',
                        transform: 'none',
                      },
                      '&:focus': {
                        color: '#344767 !important',
                      },
                      '@media screen and (max-width: 768px)': {
                        justifyContent: 'flex-start',
                        padding: 0,
                      },
                    }}
                  >
                    <StyledProfileDropdownContainer>
                      <SoftTypography
                        variant="body2"
                        sx={{
                          fontSize: '1em',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          textAlign: 'left',
                          color: '#262627',
                        }}
                      >
                        {intakeState.firstName && intakeState.lastName
                          ? `${intakeState.firstName} ${intakeState.lastName}`
                          : '(user)'}
                      </SoftTypography>
                      {/* <SoftTypography
                      variant="body2"
                      sx={[
                        {
                          fontSize: '1em',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          width: '150px',
                        },
                      ]}
                    >
                      {`${
                        selectData.agency.find(
                          ({ value }) => value === intakeState.agency
                        )?.label || '(no agency selected)'
                      }`}
                    </SoftTypography> */}
                      <IconButton
                        size="medium"
                        onClick={handleToggleProfile}
                        sx={{ padding: '0 0 0 16px' }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: '#FFF',
                            color: '#344767',
                            borderRadius: '4px',
                            boxShadow:
                              `${
                                selectData.agency.find(
                                  ({ value }) => value === intakeState.agency
                                )?.logo || ''
                              }` === ''
                                ? '0px 2px 2px rgba(0,0,0,0.2)'
                                : 'none',
                          }}
                        >
                          {`${
                            selectData.agency.find(
                              ({ value }) => value === intakeState.agency
                            )?.logo || ''
                          }` === '' ? (
                            intakeState.firstName && intakeState.lastName ? (
                              `${intakeState?.firstName?.[0]}${intakeState?.lastName?.[0]}`
                            ) : (
                              <AccountCircleIcon fontSize="small" />
                            )
                          ) : (
                            <StyledAgencyLogo
                              src={`${
                                selectData.agency.find(
                                  ({ value }) => value === intakeState.agency
                                )?.logo || ''
                              }`}
                            />
                          )}
                        </Avatar>
                      </IconButton>
                    </StyledProfileDropdownContainer>
                  </Button>

                  <Menu
                    id="differentone"
                    MenuListProps={{
                      'aria-labelledby': 'fade-button',
                    }}
                    anchorEl={accountMenu}
                    open={accountMenuIsOpen}
                    onClose={handleCloseAccountMenu}
                    TransitionComponent={Fade}
                  >
                    <MenuItem as={Link} to="/profile">
                      <ListItemIcon>
                        <AccountCircleIcon fontSize="small" />
                      </ListItemIcon>
                      Profile
                    </MenuItem>
                    <MenuItem
                      onClick={assumedUser ? unassume : signOut}
                      data-testid="signout-button"
                    >
                      <ListItemIcon>
                        <LockIcon fontSize="small" />
                      </ListItemIcon>
                      {assumedUser ? 'Back to admin account' : 'Sign Out'}
                    </MenuItem>
                  </Menu>
                </SoftBox>
              </SoftBox>
            )}
          </Toolbar>
        </AppBar>
      </span>
    </>
  )
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
}

export default DashboardNavbar
